import {
  Avatar,
  Button,
  Col,
  Dropdown,
  Image,
  Menu,
  Row,
  Skeleton,
  Space,
  Typography,
} from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useLocation } from "react-router-dom"
import { Countries } from "../constants/Countries"
import { useIsThungela } from "../hooks/useIsThungela"
import { useGetPerson, usePersonMutation } from "../network/usePerson"
import { HelpDrawer } from "./HelpDrawer"
import { NavDropdown } from "./NavDropdown"
import { SupportModal } from "./SupportModal"
import { TrainingRerquestDrawer } from "./TrainingRerquestDrawer"

export function Navbar() {
  const { pathname } = useLocation()
  const personQuery = useGetPerson({
    authenticated: !window.location.pathname.includes("auth"),
  })
  const { t, i18n } = useTranslation()
  const personMutation = usePersonMutation()

  const [visible, setVisible] = useState(false)
  const [open, setOpen] = useState(false)
  const [trainingRequest, setTrainingRequest] = useState(false)

  const [drawerFormType, setDrawerFormType] = useState(null)
  const homeRoutes = ["", "courses", "programs"]
  const exploreAllowed = [
    "Thungela-6nv82",
    "Thungela-oe94g",
    "Unitrans-t3kkx",
    "I-WIN-Demo-2lj5l",
    "ECIC-qeo37",
  ]
  const isThungela = useIsThungela(personQuery?.data?.company?.tenantId)
  if (
    pathname.split("/")[5] === "learning" ||
    (pathname.split("/")[1] === "externals" &&
      pathname.split("/")[3] === "learning")
  ) {
    return null
  }

  const communitiesAllowed = (tenantId) => {
    const seritiCompanyPeopleWhitelist = [
      "c3652e6a-aee5-43ea-9b1f-0815e49fe2b2",
      "9e1cccbc-8fbf-4d63-a85e-d1a3611de893",
      "af745a7d-c22c-4206-a20b-5c1d0a6a04c1",
      "380302c0-4230-44b5-96bc-e18408133acf",
      "e9f7d2e7-c0c6-420e-b4f8-2614799b72b8",
      "e0cc1237-1efb-4255-844a-c0fd27531568",
    ]
    if (
      tenantId === "Seriti-rivwz" &&
      (personQuery?.data?.companyPerson?.employment?.type === "Contractor" ||
        seritiCompanyPeopleWhitelist.includes(
          personQuery?.data?.companyPerson?.uid
        ))
    ) {
      return true
    }
    return false
  }

  const localeMap = {
    en: {
      country: Countries["United Kingdom"],
      name: "English",
      id: 1,
    },
    fr: {
      country: Countries.France,
      name: "French",
      id: 2,
    },
    es: {
      country: Countries.Spain,
      name: "Spanish",
      id: 3,
    },
    zh: {
      country: Countries.China,
      name: "Mandarin",
      id: 4,
    },
  }

  const generateMenuItems = (tenantId) => {
    const items = [
      {
        label: (
          <NavLink title="View all content that you're enrolled on" to="/">
            {t("myLearning")}
          </NavLink>
        ),
        key: "learning",
      },
    ]
    if (tenantId && exploreAllowed.includes(tenantId)) {
      items.push({
        label: (
          <NavLink
            title="Explore our learning library of either self-enrolled or SETA online courses"
            to="/explore"
          >
            {t("Explore")}
          </NavLink>
        ),
        key: "explore",
      })
    }
    if (tenantId) {
      items.push({
        label: (
          <NavLink
            title="Manage, review team member/s activity and make course bookings"
            to="/teams"
          >
            {t("Teams")}
          </NavLink>
        ),
        key: "teams",
      })
    }
    if (tenantId && communitiesAllowed(tenantId)) {
      items.push({
        label: (
          <a
            href="https://communities.seritilearn.com"
            rel="noopener"
            target="_blank"
          >
            Connect@Seriti
          </a>
        ),
        key: "communities",
      })
    }
    return items
  }

  const getOrgImage = (pQuery) => {
    if (pQuery?.data && !pQuery?.isLoading) {
      console.log('pQuery?.data:::', pQuery?.data)
      if (
        pQuery.data?.company?.configuration?.products &&
        Object.hasOwn(
          pQuery.data?.company?.configuration?.products,
          window.location.origin
        )
      ) {
        return pQuery.data?.company?.configuration?.products?.[
          window.location.origin
        ]?.imageUrl
      }
      if (pQuery.data?.company?.imageUrl) {
        return pQuery.data?.company?.imageUrl
      }
      return "iwinlogo.png"
    }
  }

  return (
    <Row className="navbar hide--aboveMedium">
      <SupportModal
        setVisible={setVisible}
        support={
          personQuery?.data?.company?.configuration?.products?.learn?.support
        }
        visible={visible}
      />
      <HelpDrawer
        drawerFormType={drawerFormType}
        onClose={() => setOpen(false)}
        personQuery={personQuery}
        visible={open}
      />

      {personQuery.isLoading ? (
        <Space>
          <Skeleton.Avatar />
          <Skeleton.Input />
        </Space>
      ) : (
        <>
          <Col span={6}>
            <Image
              fallback="/iwinlogo.png"
              height="2.5rem"
              preview={false}
              src={getOrgImage(personQuery)}
              style={{
                width: "2.5rem",
                objectFit: "cover",
                borderRadius: "0.625rem",
              }}
            />
          </Col>
          <Col className="hide--aboveMedium" flex={"auto"}>
            <Menu
              className="navbar__list"
              items={generateMenuItems(
                personQuery.data?.companyPerson?.company?.tenantId
              )}
              mode="horizontal"
              selectedKeys={
                homeRoutes.includes(pathname.split("/")[1])
                  ? "learning"
                  : pathname.split("/")[1]
              }
            />
          </Col>
          <Col className="nav__person" span={6}>
            <Space>
              {isThungela ? (
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item
                        key={"support query"}
                        onClick={() => {
                          setOpen(true)
                          setDrawerFormType("query")
                        }}
                      >
                        Support Query
                      </Menu.Item>
                      <Menu.Item
                        key={"Course creation request"}
                        onClick={() => {
                          setOpen(true)
                          setDrawerFormType("course")
                        }}
                      >
                        Course Creation Request
                      </Menu.Item>
                      <Menu.Item
                        key={"Content Creation Form"}
                        onClick={() => {
                          setOpen(true)
                          setDrawerFormType("content")
                        }}
                      >
                        Content Creation Form
                      </Menu.Item>
                      {/* <Menu.Item
                        key={"Training Request Form"}
                        onClick={() => {
                          setOpen(true)
                          setDrawerFormType("training")
                        }}
                      >
                        Training Request Form
                      </Menu.Item> */}
                    </Menu>
                  }
                >
                  <Button type="link">{t("Help")}</Button>
                </Dropdown>
              ) : personQuery?.data?.companyPerson?.company?.tenantId.includes(
                  "Brew"
                ) ? (
                <a href="mailto:support@brewmastersacademy.com?subject=Support%20Query&body=Query%20type%0D%0A%0D%0AAccess%0D%0A%0D%0ARegistration%0D%0A%0D%0AReporting%0D%0A%0D%0ACompletion%0D%0A%0D%0ABookings%0D%0A%0D%0AReset%20Assessment%0D%0A%0D%0AOther%20-%20Please%20explain%0D%0A%0D%0A%0D%0A%0D%0APlease%20copy%20the%20URL%20of%20the%20page%20where%20you%20are%20encountering%20the%20issue.%0D%0A%0D%0A%0D%0A%0D%0APlease%20attach%20screenshots%20of%20the%20issue%20so%20that%20we%20can%20better%20assist%20you.">
                  {t("Help")}
                </a>
              ) : (
                <a href="mailto:support@winwinza.com?subject=Support%20Query&body=Query%20type%0D%0A%0D%0AAccess%0D%0A%0D%0ARegistration%0D%0A%0D%0AReporting%0D%0A%0D%0ACompletion%0D%0A%0D%0ABookings%0D%0A%0D%0AReset%20Assessment%0D%0A%0D%0AOther%20-%20Please%20explain%0D%0A%0D%0A%0D%0A%0D%0APlease%20copy%20the%20URL%20of%20the%20page%20where%20you%20are%20encountering%20the%20issue.%0D%0A%0D%0A%0D%0A%0D%0APlease%20attach%20screenshots%20of%20the%20issue%20so%20that%20we%20can%20better%20assist%20you.">
                  {t("Help")}
                </a>
              )}
              {personQuery?.data?.company?.tenantId
                .toLowerCase()
                .includes("seriti") && (
                <a
                  href="https://storage.googleapis.com/winwin-content/Seriti videos/Seriti_Learner Guide (1).pdf"
                  rel="noopener"
                  target="_blank"
                >
                  FAQ
                </a>
              )}
              {personQuery?.data?.company?.configuration?.products?.learn
                ?.locales?.length && (
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item
                        onClick={() => {
                          i18n.changeLanguage("en")
                          personMutation.mutate(
                            {
                              data: {
                                preference: {
                                  defaultLanguageId: 1,
                                },
                              },
                            },
                            {
                              onSuccess: () => {
                                window.location.reload()
                                // queryClient.invalidateQueries()
                              },
                            }
                          )
                        }}
                      >
                        {Countries["United Kingdom"]}{" "}
                        <span style={{ marginLeft: "5px" }}>English</span>
                      </Menu.Item>
                      {personQuery?.data?.company?.configuration?.products?.learn?.locales
                        ?.filter((x) => x !== "en")
                        ?.map((locale, index) => (
                          <Menu.Item
                            key={locale + index}
                            onClick={() => {
                              i18n.changeLanguage(locale)
                              personMutation.mutate(
                                {
                                  data: {
                                    preference: {
                                      defaultLanguageId: localeMap?.[locale].id,
                                    },
                                  },
                                },
                                {
                                  onSuccess: () => {
                                    window.location.reload()
                                  },
                                }
                              )
                            }}
                          >
                            {localeMap?.[locale].country}{" "}
                            <span style={{ marginLeft: "5px" }}>
                              {localeMap?.[locale].name}
                            </span>
                          </Menu.Item>
                        ))}
                    </Menu>
                  }
                >
                  <Button loading={personMutation.isLoading} size="small">
                    {i18n.language === "en" && Countries["United Kingdom"]}
                    {i18n.language === "fr" && Countries.France}
                    {i18n.language === "zh" && Countries.China}
                    {i18n.language === "es" && Countries.Spain}
                    <span
                      style={{
                        marginLeft: "5px",
                      }}
                    >
                      {i18n.language}
                    </span>
                  </Button>
                </Dropdown>
              )}

              {isThungela && (
                <TrainingRerquestDrawer
                  setVisible={setTrainingRequest}
                  visible={trainingRequest}
                />
              )}
              <Dropdown
                overlay={
                  <NavDropdown
                    switchToAdmin={personQuery?.data?.companyPerson?.admin}
                  />
                }
                title="Manage your profile and view your progress"
              >
                <Space>
                  <Avatar src={personQuery.data?.imageUrl} />
                  <Typography.Text className="hide--onMobile" ellipsis>
                    {personQuery.data?.firstName} {personQuery.data?.lastName}
                  </Typography.Text>
                </Space>
              </Dropdown>
            </Space>
          </Col>
        </>
      )}
    </Row>
  )
}
